import type { KeyboardEvent } from "react";
import { format, parseISO } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Building, Users, AlertTriangle, TrendingUp, Info, Calendar, Play } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { SystemSummaryData, LocationSummaryData, CapacityStatus } from "@/hooks/use-capacity";
import { CapacitySummaryBar } from "./capacity-summary-bar";

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function getStatusColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive text-destructive-foreground";
    case "near":
      return "bg-amber-500 text-white dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 text-white dark:bg-emerald-600";
    case "unknown":
    default:
      return "bg-muted text-muted-foreground";
  }
}

function getStatusLabel(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "Over Capacity";
    case "near":
      return "Near Capacity";
    case "ok":
      return "OK";
    case "unknown":
    default:
      return "Unknown";
  }
}

function getStatusTooltip(status: CapacityStatus): string {
  switch (status) {
    case "ok":
      return "Utilization is within normal operating range";
    case "near":
      return "Approaching capacity limits — monitor closely";
    case "over":
      return "Exceeding configured capacity — immediate attention needed";
    case "unknown":
    default:
      return "Capacity data is not available or not yet configured";
  }
}

export { getStatusColor, getStatusLabel, getStatusTooltip };

function getProgressColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive";
    case "near":
      return "bg-amber-500 dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 dark:bg-emerald-600";
    default:
      return "bg-muted";
  }
}

interface SnapshotSummaryData {
  occupancyAllKennels: number;
  dataAsOf: string | null;
  staleLocationCount: number;
  currentLocationCount: number;
}

interface SystemCapacitySummaryProps {
  data: SystemSummaryData | undefined;
  snapshotData?: SnapshotSummaryData | null;
  isAdmin?: boolean;
  snapshotsEnabled?: boolean;
}

export function SystemCapacitySummary({ data, snapshotData, isAdmin = false, snapshotsEnabled }: SystemCapacitySummaryProps) {
  if (!data) return null;

  const utilizationValue = data.utilizationConfigured ?? 0;
  const hasUtilization = data.utilizationConfigured !== null;
  
  // Use snapshot population if available for consistency with breakdown cards
  const displayPopulation = snapshotData?.occupancyAllKennels ?? data.occupancyAllZones;

  return (
    <Card className="mb-6" data-testid="card-system-summary">
      <CardContent className="py-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
              <span className="text-lg font-semibold" data-testid="text-system-title">
                System-wide Overview
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Badge 
                      className={getStatusColor(data.status)}
                      data-testid="badge-system-status"
                    >
                      {getStatusLabel(data.status)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getStatusTooltip(data.status)}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              {snapshotsEnabled && (
                <Badge 
                  className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="badge-snapshots-enabled"
                >
                  <Play className="h-3 w-3" />
                  <span>Snapshots Enabled</span>
                </Badge>
              )}
            </div>
          </div>

          {hasUtilization ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>
                  <strong data-testid="text-system-occupancy">{displayPopulation}</strong> animals across all locations
                </span>
                <span className="font-medium" data-testid="text-system-utilization">
                  {utilizationValue}%
                </span>
              </div>
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
                <div 
                  className={`h-full transition-all ${getProgressColor(data.status)}`}
                  style={{ width: `${Math.min(utilizationValue, 100)}%` }}
                  data-testid="progress-system-utilization"
                />
              </div>
            </div>
          ) : (
            <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-500" />
              <AlertDescription className="text-amber-700 dark:text-amber-300" data-testid="text-no-utilization">
                {displayPopulation} animals across all locations (no capacity configured)
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              <span data-testid="text-system-coverage">
                Utilization based on <strong className="text-foreground">{data.zonesWithCapacity}</strong> of {data.zonesTotal} kennels
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface LocationCapacityCardProps {
  data: LocationSummaryData;
  isSelected: boolean;
  onClick: () => void;
}

export function LocationCapacityCard({ data, isSelected, onClick }: LocationCapacityCardProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Card 
      className={`cursor-pointer hover-elevate transition-all ${isSelected ? "ring-2 ring-primary" : ""}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      data-testid={`card-location-${data.location.toLowerCase()}`}
    >
      <CardContent className="py-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-semibold" data-testid={`text-location-label-${data.location.toLowerCase()}`}>
              {data.locationLabel}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Badge 
                    className={getStatusColor(data.status)}
                    data-testid={`badge-location-status-${data.location.toLowerCase()}`}
                  >
                    {getStatusLabel(data.status)}
                  </Badge>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getStatusTooltip(data.status)}</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <CapacitySummaryBar
            totalAnimals={data.occupancyAllZones}
            utilizationPercent={data.utilizationConfigured}
            zonesWithCapacity={data.zonesWithCapacity}
            zonesTotal={data.zonesTotal}
            status={data.status}
            testIdPrefix={`location-${data.location.toLowerCase()}`}
          />

          {data.noReportsWarning && (
            <div className="flex items-center justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-amber-500 text-xs">
                    <AlertTriangle className="h-3 w-3" />
                    <span data-testid={`text-location-no-reports-${data.location.toLowerCase()}`}>No reports</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>No completed reports found</p>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface LocationCapacityGridProps {
  children: React.ReactNode;
}

export function LocationCapacityGrid({ children }: LocationCapacityGridProps) {
  return (
    <div 
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6"
      data-testid="grid-location-summaries"
    >
      {children}
    </div>
  );
}
